import { inject, Injectable } from '@angular/core';
import { Brand, Category, normilizeBrandImage } from '@models';
import { toSignal } from '@angular/core/rxjs-interop';
import { MENU_BRANDS_COUNT } from '@core';
import { INavigationTree } from '@navigation';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api-service';
import { APPLE_STORE_PAGES_ENABLED } from '../constants/apple-store.constant';

@Injectable({
  providedIn: 'root',
})
export class HeaderMenuService {
  catIdForWarranty: number = 60000;
  catIdForTrademark: number = 60001;

  private readonly apiService = inject(ApiService);

  categoriesView = toSignal(this.getCategoriesRequest());
  brandsView = toSignal(this.getBrandsRequest().pipe(map((items) => items.map(normilizeBrandImage))));

  private getCategoriesRequest(): Observable<INavigationTree[]> {
    return this.apiService.get('brandCategory/getCategories').pipe(
      map((response) => {
        const categories = response as Category[];
        const filteredCategories = categories.filter((category) => category.id !== this.catIdForWarranty);
        const restructuredCategories = this.moveAppleToTopLevel(filteredCategories);
        const categoriesWithTrademark = this.addTrademarkToCategories(restructuredCategories);

        return this.convertCategoriesToNavigationTree(categoriesWithTrademark);
      }),
    );
  }

  public getBrandsRequest(): Observable<Brand[]> {
    // ToDo: replace this with the correct API client
    return this.apiService.get('brands', { pageSize: MENU_BRANDS_COUNT }).pipe(map((res: any) => res.items));
  }

  private moveAppleToTopLevel(categories: Category[]): Category[] {
    let appleCategory: Category | null = null;

    // Find and extract Apple category from Electronics
    const updatedCategories = categories.map((category) => {
      if (category.id === 11000) {
        // Electronics category
        const electronicsSubCategories =
          category.subCategories?.filter((subCategory) => {
            if (subCategory.id === this.catIdForTrademark) {
              appleCategory = subCategory;
              return false; // Remove from Electronics subcategories
            }
            return true;
          }) || [];

        return {
          ...category,
          subCategories: electronicsSubCategories,
        };
      }
      return category;
    });

    // Add Apple as top-level category at the beginning if found
    if (appleCategory) {
      return [appleCategory, ...updatedCategories];
    }

    return updatedCategories;
  }

  private addTrademarkToCategories(categories: Category[]): Category[] {
    return categories.map((category) => {
      const updatedCategory = { ...category };

      // Add trademark symbol only to the category with id 60001
      if (category.id === this.catIdForTrademark) {
        updatedCategory.name = `${category.name}®`;
        // Keep subcategories as-is without processing them for trademark
        return updatedCategory;
      }

      // Recursively handle subcategories for other categories
      if (category.subCategories && category.subCategories.length > 0) {
        updatedCategory.subCategories = this.addTrademarkToCategories(category.subCategories);
      }

      return updatedCategory;
    });
  }

  private convertCategoriesToNavigationTree(categories: Category[] = []): INavigationTree[] {
    if (!categories) {
      return [];
    }
    return categories.map((category: any) => {
      return {
        id: category.id,
        name: category.name,
        pid: category.pId,
        group: this.convertCategoriesToNavigationTree(category.subCategories),
      };
    });
  }

  /**
   * Determines whether an Apple category should use the special Apple category page
   * or the regular category page navigation
   */
  public shouldUseAppleCategoryPage(categoryId: number): boolean {
    if (!APPLE_STORE_PAGES_ENABLED) {
      return false;
    }

    const appleCategoryPageIds = [
      60001, // Apple main
      60002, // Mac
      60003, // iPad
      60004, // iPhone
      60005, // Watch
      60007, // AirPods
      60008, // TV & Home
    ];

    return appleCategoryPageIds.includes(categoryId);
  }

  /**
   * Get all Apple category IDs (both special and regular navigation)
   */
  public getAllAppleCategoryIds(): number[] {
    return [
      60001, // Apple main
      60002, // Mac
      60003, // iPad
      60004, // iPhone
      60005, // Watch
      60006, // Beats Headphones
      60007, // AirPods
      60008, // TV & Home
      60009, // Entertainment
      60010, // Accessories
      62003, // AirTag
      // Add other specific Apple subcategory IDs as needed
    ];
  }

  /**
   * Centralized Apple navigation logic
   */
  public navigateToAppleCategory(categoryId: number, navigationService: any, navigationPath?: any[]): void {
    if (this.shouldUseAppleCategoryPage(categoryId)) {
      navigationService.goToAppleCategoryPage(categoryId);
    } else {
      navigationService.goToCategoryPage(
        categoryId,
        navigationPath ? { data: JSON.stringify(navigationPath) } : undefined,
      );
    }
  }
}
